import * as React from 'react';
import { styled } from '@mui/material/styles';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { dataConstants } from '../../constants/index.js';
import { getRandomStr } from '../../helpers/index.js';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { Box, Chip, Grid, IconButton, Paper } from '@mui/material';
import TextField from '@mui/material/TextField';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { levenshtein } from '../../utils/levenshtein/index.js';
import { useRouter } from "next/router";
import BookmarkAddedRoundedIcon from '@mui/icons-material/BookmarkAddedRounded';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
// const ListItem = styled('li')(({ theme }) => ({
//   margin: '4px 8px 4px 0',
// }));

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    // color: theme.palette.primary.main,
    color: "#2a2d64",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    // backgroundColor: theme.palette.primary.main,
    backgroundColor: "#2a2d64",
    fontWeight: 600,
    color: '#fff',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    padding: '5px 15px',
  },
}));

const QuickSearchField = styled((props) => (
  <TextField
    {...props}
  />
))(({ theme }) => ({
  [theme.breakpoints.down('xs')]: {
    width: '100%',
  },
  margin: '0 12px 4px 12px',
  '& .MuiSvgIcon-root': {
    marginRight: '4px',
    color: 'hsl(235deg 59.62% 21.27%)' /*#020B4A*/
  },
  '& .MuiInputBase-input-MuiInput-input:hover': {
    background: '#f0f1f4',
  },
  border: `1px solid hsla(216, 41%, 87%, 0.8)`,
  borderRadius: '7px',
  minWidth: '100%',
  '& .Mui-focused': {
    background: '#fff',
    borderRadius: '7px',
  },
  '& .MuiInput-underline:before': {
    padding: '4px',
    border: `0 !important`,
  },
  '& .MuiInput-underline:after': {
    // border: `1px solid ${theme.palette.text.link}`,
    border: `0 !important`,
  },
  '& .MuiInputBase-input-MuiInput-input': {
    fontSize: '.8rem !important',
  },
  '& .MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled):before': {
    background: '#fff',
  }
}));

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  // border: `1px solid ${theme.palette.divider}`,
  background: '#fbfbfc',
  border: '1px solid rgb(229, 234, 242)',
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:first-child': {
    borderRadius: '7px 7px 0 0',
  },
  '&:last-child': {
    borderRadius: '0 0 7px 7px',
  },
  '&:last-child .MuiAccordionDetails-root': {
    borderRadius: '0 0 7px 7px',
  },
  '& .MuiAccordionDetails-root': {
    padding: '12px',
    background: '#fff'
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={props?.user ? <BookmarkBorderRoundedIcon sx={{ fontSize: '1.2rem', color: '#2d4156' }} /> :
      <BookmarkAddedRoundedIcon sx={{ fontSize: '1.2rem', color: '#2d4156' }} />}
    // startIcon={<CloseRoundedIcon sx={{ color: 'rgba(0, 0, 0, 0.26) !important' }} />}
    {...props}
  />
))(({ theme }) => ({
  color: '#2d4156',
  minHeight: "37px",
  fontSize: '.875rem',
  // backgroundColor:
  //   theme.palette.mode === 'dark'
  //     ? 'rgba(255, 255, 255, .05)'
  //     : '#fbfbfc',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(0deg)',
  },
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(1),
  },

}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgb(229, 234, 242)',
}));

const findClosestMatch = (watchlists, query) => {
  const closestMatch = watchlists.reduce((closest, item) => {
    const shortNameDistance = levenshtein(query, item.name.toLowerCase());

    if (shortNameDistance < closest.distance) {
      return {
        distance: Math.min(shortNameDistance),
        item,
      };
    }

    return closest;
  }, { distance: Number.MAX_SAFE_INTEGER, item: null });

  return closestMatch.item;
}

export default function UserWatchlists({ system_watchlists, user_watchlists, selectedWatchlist, isSystemDefault, sx }) {
  const router = useRouter();
  const [userWatchlists, setUserWatchlists] = React.useState(user_watchlists);
  const [systemWatchlists, setSystemWatchlists] = React.useState(system_watchlists);
  const [searchText, setSearchText] = React.useState('');
  const [searchTextErr, setSearchTextErr] = React.useState('');
  const [suggestion, setSuggestion] = React.useState(null);
  const _user_watchlists = user_watchlists?.filter(item => !item.isSystemDefault);

  React.useEffect(() => {
    setUserWatchlists(user_watchlists?.filter(item => !item.isSystemDefault))
    setSystemWatchlists(system_watchlists)
  }, [system_watchlists, user_watchlists])

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchText(query);

    if (query.length === 0) {
      setSuggestion(null);
      setSystemWatchlists(system_watchlists);
      setUserWatchlists(user_watchlists?.filter(item => !item.isSystemDefault));
      return;
    }

    const all_lists = _user_watchlists && _user_watchlists.length > 0 ? [..._user_watchlists, ...system_watchlists] : system_watchlists;
    const filteredSItems = system_watchlists?.filter(item => item.name.toLowerCase().includes(query));
    const filteredUItems = user_watchlists?.filter(item => item.name.toLowerCase().includes(query));

    if (filteredUItems.length === 0 || filteredSItems === 0) {
      // No exact match found, try to find the closest match
      const closestMatch = findClosestMatch(all_lists, query);
      setSuggestion(closestMatch);
      setSearchTextErr(`Your search - <b>${e?.target?.value}</b> - did not match any of your watchlists. Search instead for <i><b><a href="/watchlists/${closestMatch?.name}">${closestMatch?.name}</a></b></i>`)
    } else {
      setSuggestion(null);
    }

    setSystemWatchlists(filteredSItems.length > 0 ? filteredSItems : system_watchlists);
    setUserWatchlists(filteredUItems.length > 0 ? filteredUItems : _user_watchlists);
  };

  const clearSearch = () => {
    setSearchText('');
    setSuggestion(null);
    setSystemWatchlists(system_watchlists);
    setUserWatchlists(_user_watchlists);
  };

  // React.useEffect(() => {
  //   const uniqueSectors = Array.from(new Set(filteredIndustries.map(item => item.sector))).sort();
  //   setExpanded(uniqueSectors[0]?.toLowerCase()?.replace(/ /g, '_'))
  // }, [filteredIndustries])

  // const handleAccordion = (panel) => (event, isExpanded) => {
  //   setExpanded(isExpanded ? panel : false);
  // };

  return (
    <>
      <Grid item xs={12} sm={3} md={2.5}
        sx={{
          '& .MuiTextField-root.searchField': {
            minWidth: '100% !important',
            mt: '16px !important',
            mb: '14px !important',
          }, ...sx
        }}>
        <QuickSearchField
          variant="standard"
          value={searchText}
          onChange={handleSearch}
          placeholder="Search through all watchlists.."
          className="searchField"
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ ml: 1 }} />,
            endAdornment: (
              <IconButton
                title="Clear"
                aria-label="Clear"
                size="small"
                style={{ visibility: searchText ? 'visible' : 'hidden' }}
                onClick={clearSearch}
                sx={{ '& .MuiSvgIcon-fontSizeSmall': { marginRight: '0 !important' } }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ),
          }}
          sx={{
            // mr: 2,
            // mb: 2,
            background: '#fff',
            '&.MuiTextField-root.searchField': {
              m: '0px 12px 0px 0px',
            },
            '& .MuiSvgIcon-root': {
              fontSize: '1.1rem',
            },
            '& .MuiInputBase-input::placeholder': {
              fontSize: '.87rem',
            },
            '& .MuiInputBase-input': {
              fontSize: '.87rem',
              padding: '7px'
            },
          }}
        />
        {searchTextErr !== '' && suggestion &&
          <Typography variant="body1" component="div" sx={{ fontSize: '.875rem', px: .5, mb: 1.5, }} dangerouslySetInnerHTML={{ __html: searchTextErr }} />}
        <Box component="div" className="industry-wrapper" sx={{}}>
          {systemWatchlists && systemWatchlists.length > 0 ?
            <Accordion expanded={true} >
              <AccordionSummary aria-controls={`system-watchlist-content`} id={`system-watchlist-header`}>
                Watchlists by Grufity
              </AccordionSummary>
              <AccordionDetails>
                {systemWatchlists?.map(d => {
                  const _tickers = ((d?.tickers?.replace(/[\[\]"]+/g, '')?.replace(/,/g, ', ') || '').split(", ") || []).slice(0, 13).join(', ') + (d?.tickers && (d.tickers.match(/,/g) || []).length > 12 ? ` and ${d.tickers.match(/,/g).length - 12} more.` : '');
                  return (d?.isSystemDefault ?
                    <LightTooltip key={getRandomStr(5)}
                      title={<React.Fragment>
                        <Typography color="inherit" variant="caption" sx={{ fontWeight: 600 }}>Tickers: </Typography>
                        <br />{_tickers?.replace(/undefined,/, "")}
                      </React.Fragment>}
                      placement="left" disableInteractive>
                      <Chip
                        // component="a"
                        onClick={() => router.push(`/watchlists/${d?.name}`)}
                        // clickable
                        // size="small"
                        variant="outlined"
                        label={d?.name}
                        // onClick={available ? handleDelete(d) : handleInsert(d)}
                        // onDelete={available ? handleDelete(d) : undefined}
                        deleteIcon={<CloseRoundedIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.26) !important', fontSize: '1.1rem !important' }} />}
                        sx={{
                          '&:hover': { background: 'rgb(180 186 193 / 37%) !important' },
                          '&:hover .MuiChip-label': { color: "rgba(0, 0, 0, 0.87)" },
                          m: .45, borderRadius: '5px', border: '1px solid hsla(216, 41%, 87%, 0.8)', fontSize: '.8rem', height: "26px",
                          bgcolor: selectedWatchlist === d?.name && isSystemDefault ? 'rgb(180 186 193 / 37%)!important' : '', '& .MuiChip-label': { color: selectedWatchlist === d?.name ? "rgba(0, 0, 0, 0.87)" : 'rgb(67, 65, 102)', px: 1 }
                        }}
                      />
                    </LightTooltip> : null)
                })}
              </AccordionDetails>
            </Accordion> : null}
          {userWatchlists && userWatchlists.length > 0 ?
            <Accordion expanded={true} >
              <AccordionSummary aria-controls={`user-watchlist-content`} id={`user-watchlist-header`} user={true}>
                Your Watchlists
              </AccordionSummary>
              <AccordionDetails>
                {userWatchlists?.map(d => {
                  const _tickers = ((d?.tickers?.replace(/[\[\]"]+/g, '')?.replace(/,/g, ', ') || '').split(", ") || []).slice(0, 13).join(', ') + (d?.tickers && (d.tickers.match(/,/g) || []).length > 12 ? ` and ${d.tickers.match(/,/g).length - 12} more.` : '');
                  return (d?.isSystemDefault ? null :
                    <LightTooltip key={getRandomStr(5)}
                      title={_tickers && _tickers !== "" ?
                        <React.Fragment>
                          <Typography color="inherit" variant="caption" sx={{ fontWeight: 600 }}>Tickers: </Typography>
                          <br />{_tickers?.replace(/undefined,/, "")}
                        </React.Fragment> : ""}
                      placement="left" disableInteractive>
                      <Chip
                        // component="a"
                        onClick={() => {
                          // handleUserWatchlist(d?.name, d?.tickers && d?.tickers !== "" ? JSON.parse(d?.tickers) : []);
                          router.push(`/watchlists/u/${d?.name}`)
                        }}
                        // clickable
                        // size="small"
                        variant="outlined"
                        label={d?.name}
                        // onClick={available ? handleDelete(d) : handleInsert(d)}
                        // onDelete={available ? handleDelete(d) : undefined}
                        deleteIcon={<CloseRoundedIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.26) !important', fontSize: '1.1rem !important' }} />}
                        sx={{
                          '&:hover': { background: 'rgb(180 186 193 / 37%) !important' },
                          '&:hover .MuiChip-label': { color: "rgba(0, 0, 0, 0.87)" },
                          m: .45, borderRadius: '5px', border: '1px solid hsla(216, 41%, 87%, 0.8)', fontSize: '.8rem', height: "26px",
                          bgcolor: selectedWatchlist === d?.name && !isSystemDefault ? 'rgb(180 186 193 / 37%)!important' : '', '& .MuiChip-label': { color: selectedWatchlist === d?.name ? "rgba(0, 0, 0, 0.87)" : 'rgb(67, 65, 102)', px: 1 }
                        }}
                      />
                    </LightTooltip>)
                })}
              </AccordionDetails>
            </Accordion> : null}
        </Box>
      </Grid>
    </>
  )
}
